'use client'

import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { authApi } from '../api/endpoints'
import { useTelegramBotAPI } from '../hooks/useTelegramBotAPI'
import { useAuthContext } from '../contexts/AuthContext'
import { useT } from '../contexts/LocaleContext'
import LuckyIcon from '../components/lucky/LuckyIcon'
import LanguagePicker from '../components/lucky/LanguagePicker'
import {
  consumePendingDeepLink,
  isDeepLinkStartParam,
  rememberDeepLink,
} from '../lib/telegramDeepLink'

export default function RegisterPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const t = useT()
  const { user: tgUser, initData, startParam, showAlert, ready } = useTelegramBotAPI()
  const { register, isAuthenticated, isLoading: authLoading } = useAuthContext()
  const [referralCode, setReferralCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [gettingCode, setGettingCode] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pendingTeamLink, setPendingTeamLink] = useState(false)

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/', { replace: true })
    }
  }, [authLoading, isAuthenticated, navigate])

  useEffect(() => {
    if (location.state && typeof location.state === 'object' && 'referralCode' in location.state) {
      const v = (location.state as { referralCode: string }).referralCode
      if (v) setReferralCode(v.toUpperCase())
      return
    }
    if (!startParam) return
    if (isDeepLinkStartParam(startParam)) {
      rememberDeepLink(startParam)
      setPendingTeamLink(true)
      return
    }
    setReferralCode(startParam.toUpperCase())
  }, [location.state, startParam])

  const handleRegister = async () => {
    if (!tgUser || !initData) {
      setError(t('register.noTgData'))
      return
    }

    setLoading(true)
    setError(null)

    try {
      const result = await register(referralCode.trim())
      if (result.success) {
        const dest = consumePendingDeepLink(startParam)
        navigate(dest || '/')
      } else {
        setError(result.error || t('register.registerFail'))
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t('register.registerErr'))
    } finally {
      setLoading(false)
    }
  }

  const handleGetReferralCode = async () => {
    setGettingCode(true)
    setError(null)
    try {
      const { code } = await authApi.getRandomReferralCode()
      setReferralCode(code)
    } catch {
      showAlert(t('register.codeFetchErr'))
    } finally {
      setGettingCode(false)
    }
  }

  if (authLoading || isAuthenticated) {
    return (
      <div className="lucky-auth mini-app-container min-h-screen flex items-center justify-center">
        <div className="lucky-auth__empty">{t('common.loading')}</div>
      </div>
    )
  }

  if (!tgUser) {
    return (
      <div className="lucky-auth mini-app-container min-h-screen flex items-center justify-center p-4">
        <div className="lucky-auth__card lucky-auth__card--warn">{t('register.needTelegram')}</div>
      </div>
    )
  }

  const channelUrl = 'https://t.me/sisil_org'

  return (
    <div className="lucky-auth mini-app-container pb-20 min-h-screen">
      <div className="lucky-auth__inner">
        <header className="lucky-auth__hero">
          <div className="lucky-auth__logo">
            <LuckyIcon name="logo" size={56} alt="" />
          </div>
          <h1>Sisil</h1>
          <p>{t('register.subtitle')}</p>
          {pendingTeamLink && <em className="lucky-auth__note">{t('register.pendingTeam')}</em>}
        </header>

        <LanguagePicker />

        <section className="lucky-auth__card">
          <h2>{t('register.noCodeTitle')}</h2>
          <p>{t('register.noCodeHint')}</p>
          <a href={channelUrl} target="_blank" rel="noopener noreferrer" className="lucky-auth__link" dir="ltr">
            {channelUrl}
          </a>
        </section>

        {error && <div className="lucky-auth__error">{error}</div>}

        <section className="lucky-auth__card">
          <label className="lucky-auth__label">
            {t('register.referralLabel')}
            <div className="lucky-auth__code-row">
              <input
                type="text"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                placeholder={t('register.referralPlaceholder')}
                className="lucky-auth__input"
                maxLength={32}
                autoComplete="off"
                autoFocus
                dir="ltr"
              />
              <button
                type="button"
                className="lucky-auth__btn-sec"
                onClick={handleGetReferralCode}
                disabled={gettingCode}
                title={t('register.getRandomCode')}
              >
                {gettingCode ? '…' : '↻'}
              </button>
            </div>
          </label>

          <button
            type="button"
            className="lucky-auth__cta"
            onClick={handleRegister}
            disabled={loading || !referralCode.trim() || !ready}
          >
            {loading ? t('register.submitting') : t('register.submit')}
          </button>
        </section>

        <p className="lucky-auth__foot">{t('register.foot')}</p>
      </div>
    </div>
  )
}
